import i18n from 'i18next';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import dayjs from 'dayjs';

import TextCopy from '../../../components/TextCopy';
import EmptyData from '../../../components/empty-data/EmptyData';
import Header from '../../../components/header/Header';
import CenterLoading from '../../../components/CenterLoading/CenterLoading';
import TextBreaks from '../../../components/TextBreaks/TextBreaks';
import CustomAntButton from '../../../components/CustomAntButton/CustomAntButton';
import useLocalizedMoney from '../../../hooks/useLocalizedMoney';
import useLocalizedTime from '../../../hooks/useLocalizedTime';
import useRefundTicket from '../../../hooks/useRefundTicket';
import { getValueByLanguage, isEmptyObject, isNumber, isReserveTicket } from '../../../utils';
import styles from './styles.module.css';
import { OrderTicket } from 'types';

/**
 * 図7-1 払い戻し内容の確認画面
 */
const BoughtTicketRefundScreen = () => {
    const location = useLocation();
    const { orderTicket }: { orderTicket: OrderTicket } = location?.state ?? {};
    const { t } = useTranslation();
    const { formatTime } = useLocalizedTime();
    const { formatMoney } = useLocalizedMoney();
    const {
        loading,
        confirmModalOpen,
        RefundConfirmModal,
        ApplePayConfirmModal: ApplePayRefundConfirmModal,
        WarningConfirmModal,
    } = useRefundTicket();
    const { language } = i18n;

    const isBeforeReserveOn = useMemo(() => {
        if (isEmptyObject(orderTicket)) return false;
        const isBeforeReserveOn = orderTicket.comboItems.some((item) => {
            const reserveOn = item.orderUsageTickets?.[0]?.reservationInfo?.reserveOn;
            if (!reserveOn) {
                return false;
            }
            return item.isReserve && !dayjs().isAfter(dayjs(reserveOn));
        });
        return isBeforeReserveOn;
    }, [orderTicket]);

    return (
        <>
            {loading && <CenterLoading />}
            <RefundConfirmModal orderTicket={orderTicket} />
            <ApplePayRefundConfirmModal orderTicket={orderTicket} />
            <WarningConfirmModal />
            <Header title={t('header.ticket-detail')} />
            <div className={`${styles.wrapper} content-container scrollable`}>
                {orderTicket != null ? (
                    <div className={styles.ticketInfo}>
                        <div className={styles.infoGroup}>
                            <div className={styles.confirmation}>{t('ticket-detail.refund-confirmation')}</div>
                        </div>
                        <div className={styles.infoGroup}>
                            <div className={styles.companyName}>
                                <TextBreaks text={getValueByLanguage(orderTicket.sellerCompanyName, language)} />
                            </div>
                            <div className={styles.ticketName}>
                                <TextBreaks text={getValueByLanguage(orderTicket.ticketName, language)} />
                            </div>
                            <div className={styles.subtitle}>
                                <TextBreaks text={getValueByLanguage(orderTicket.subtitle, language)} />
                            </div>
                        </div>
                        <div className={styles.infoGroup}>
                            <div className={styles.textContent}>
                                <span className={styles.label}>{t('ticket-detail.purchased-date')}</span>
                                <span className={styles.divider}>:</span>
                                <div className={styles.value}>{formatTime(orderTicket.purchasedAt, 'lll')}</div>
                            </div>
                            <div className={styles.textContent}>
                                <span className={styles.label}>{t('ticket-detail.number-label')}</span>
                                <span className={styles.divider}>:</span>
                                <div className={styles.value}>
                                    {orderTicket.numberAdults > 0 && (
                                        <span className={styles.numberContext}>
                                            {t('ticket-detail.number', {
                                                label: t('ticket-detail.adult-label'),
                                                number: orderTicket.numberAdults,
                                            })}
                                        </span>
                                    )}
                                    {orderTicket.numberCustom1 > 0 && (
                                        <span className={styles.numberContext}>
                                            {t('ticket-detail.number', {
                                                label: getValueByLanguage(orderTicket.custom1Name, language),
                                                number: orderTicket.numberCustom1,
                                            })}
                                        </span>
                                    )}
                                    {orderTicket.numberCustom2 > 0 && (
                                        <span className={styles.numberContext}>
                                            {t('ticket-detail.number', {
                                                label: getValueByLanguage(orderTicket.custom2Name, language),
                                                number: orderTicket.numberCustom2,
                                            })}
                                        </span>
                                    )}
                                    {orderTicket.numberCustom3 > 0 && (
                                        <span className={styles.numberContext}>
                                            {t('ticket-detail.number', {
                                                label: getValueByLanguage(orderTicket.custom3Name, language),
                                                number: orderTicket.numberCustom3,
                                            })}
                                        </span>
                                    )}
                                </div>
                            </div>

                            {(orderTicket.amount ?? 0) > 0 && (
                                <div className={styles.textContent}>
                                    <span className={styles.label}>{t('ticket-detail.price')}</span>
                                    <span className={styles.divider}>:</span>
                                    <div className={styles.value}>{formatMoney(orderTicket.amount, 'JPY')}</div>
                                </div>
                            )}
                        </div>
                        <div className={styles.infoGroup}>
                            <div className={styles.textContent}>
                                <span className={styles.label}>{t('ticket-detail.orderId')}</span>
                                <span className={styles.divider}>:</span>
                                <div className={styles.value}>
                                    <TextCopy>{orderTicket.orderId}</TextCopy>
                                </div>
                            </div>
                        </div>
                        <div className={styles.infoGroup}>
                            {isNumber(orderTicket.amount) && (
                                <div className={styles.textContent}>
                                    <span className={styles.label}>{t('ticket-detail.salesAmount')}</span>
                                    <span className={styles.divider}>:</span>
                                    <div className={styles.value}>{formatMoney(orderTicket.amount, 'JPY')}</div>
                                </div>
                            )}
                            {isNumber(orderTicket.refundFee) && (
                                <div className={styles.textContent}>
                                    <span className={styles.label}>{t('ticket-detail.refundFee')}</span>
                                    <span className={styles.divider}>:</span>
                                    <div className={styles.value}>{formatMoney(orderTicket.refundFee, 'JPY')}</div>
                                </div>
                            )}
                            {isReserveTicket(orderTicket) && isNumber(orderTicket.cancellationFee) && (
                                <div className={styles.textContent}>
                                    <span className={styles.label}>{t('ticket-detail.cancellationFee')}</span>
                                    <span className={styles.divider}>:</span>
                                    <div className={styles.value}>
                                        {formatMoney(orderTicket.cancellationFee, 'JPY')}
                                    </div>
                                </div>
                            )}
                            {isNumber(orderTicket.refundAmount) && (
                                <div className={styles.textContent}>
                                    <span className={styles.label}>{t('ticket-detail.refundAmount')}</span>
                                    <span className={styles.divider}>:</span>
                                    <div className={styles.value}>{formatMoney(orderTicket.refundAmount, 'JPY')}</div>
                                </div>
                            )}
                        </div>
                        {isBeforeReserveOn && (
                            <div className={styles.reservationNote}>
                                <TextBreaks text={t('ticket-detail.reservation-ticket-note')} />
                            </div>
                        )}
                        <CustomAntButton type="outlined" className={styles.refundBtn} block onClick={confirmModalOpen}>
                            {t('button.refund')}
                        </CustomAntButton>
                    </div>
                ) : (
                    <EmptyData />
                )}
            </div>
        </>
    );
};

export default BoughtTicketRefundScreen;
